import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { DashboardModalConfig, DashboardModalsProviderProps } from './dashboardProvider.types';
import { DashboardModalsContextData } from '../context/dashboardContext.types';

const DashboardModalsContext = createContext<DashboardModalsContextData | null>(null);

export const useDashboardModals = (): DashboardModalsContextData => {
  const context = useContext(DashboardModalsContext);
  if (!context) {
    throw new Error('useDashboardModals must be used within DashboardModalsProvider');
  }
  return context;
};

interface ModalState<T = unknown> {
  isOpen: boolean;
  data?: T;
}

interface EnhancedDashboardModalsProviderProps extends DashboardModalsProviderProps {
  globalData?: Record<string, unknown>;
}

const DashboardModalsProvider: React.FC<EnhancedDashboardModalsProviderProps> = ({ modals, children, globalData }) => {
  const [modalStates, setModalStates] = useState<Record<string, ModalState>>({});

  const openModal = useCallback(
    (modalKey: string, data?: unknown) => {
      const modal = modals.find(m => m.key === modalKey);
      if (modal?.callbacks?.onOpen) {
        modal.callbacks.onOpen(data);
      }
      setModalStates(prev => ({
        ...prev,
        [modalKey]: { isOpen: true, data },
      }));
    },
    [modals],
  );

  const closeModal = useCallback((modalKey?: string) => {
    if (modalKey) {
      setModalStates(prev => ({
        ...prev,
        [modalKey]: { isOpen: false, data: undefined },
      }));
    } else {
      setModalStates({});
    }
  }, []);

  const isModalOpen = useCallback(
    (modalKey: string) => {
      return modalStates[modalKey]?.isOpen || false;
    },
    [modalStates],
  );

  const getModalData = useCallback(
    (modalKey: string) => {
      return modalStates[modalKey]?.data;
    },
    [modalStates],
  );

  const contextValue: DashboardModalsContextData = useMemo(
    () => ({
      openModal,
      closeModal,
      isModalOpen,
      getModalData,
    }),
    [openModal, closeModal, isModalOpen, getModalData],
  );

  return (
    <DashboardModalsContext.Provider value={contextValue}>
      {children}
      {modals.map((modal: DashboardModalConfig) => {
        const modalState = modalStates[modal.key];
        const ModalComponent = modal.component;
        const modalCallbacks = modal.callbacks;

        const getAfterSubmit = () => {
          if (modalCallbacks && 'afterSubmit' in modalCallbacks && modalCallbacks.afterSubmit) {
            return {
              execute: (response: unknown) => {
                modalCallbacks.afterSubmit!.execute(response);
                closeModal(modal.key);
              },
            };
          }
          return undefined;
        };

        if (!modalState?.isOpen) {
          return null;
        }

        return (
          <ModalComponent
            key={modal.key}
            isOpen
            onClose={() => closeModal(modal.key)}
            data={modalState?.data}
            globalData={globalData}
            getDataFunc={modalCallbacks && 'getDataFunc' in modalCallbacks ? modalCallbacks.getDataFunc : undefined}
            submitFormFunc={modalCallbacks?.onSubmit}
            getValidationSchemaFunc={
              modalCallbacks && 'getValidationSchema' in modalCallbacks ? modalCallbacks.getValidationSchema : undefined
            }
            afterSubmit={getAfterSubmit()}
            {...(modal.config || {})}
          />
        );
      })}
    </DashboardModalsContext.Provider>
  );
};

export default DashboardModalsProvider;
